import { Alert, Box, Dialog, DialogContent, DialogTitle, LinearProgress } from "@mui/material";
import Paper from "@mui/material/Paper";
import { observer } from "mobx-react";
import * as React from "react";
import * as api from "@crochik/pi-api";

import { toJS } from "mobx";
import App from "../../../application/App";
import { Default } from "../../../context/AppContext";
import { Action } from "../../../context/IForm";
import { IActionMenuItem } from "../../../context/IMenu";
import { DataView } from "../../DataView";
import { FileDropTarget } from "../../FileDropTarget";
import { Form } from "../../Form";
import DataViewToolbarComponent from "../DataViewToolbarComponent";
import { FormBody } from "../FormBody/FormBody";
import { FormTitle } from "../FormTitle";
import { Loading } from "../Loading";
import { DataGridProps } from "./DataGridProps";
import { DataViewFilterFormAutocomplete } from "./DataViewFilterFormAutocomplete";
import { DataViewContext, IDataViewContext, IOnClickArgs } from "./DatavViewContext";
import { IEditEvent } from "./IEditEvent";
import { LocalFilterDialog } from "./LocalFilterDialog";
import { LocalFilterPanel } from "./LocalFilterPanel";
import { ObjectPopupMenu } from "./ObjectPopupMenu";
import { parseResponseError } from "../../../api/Client";
import { URI } from "../../../api/URI";

interface Props extends DataGridProps {}

interface State {
    error?: string;
    objectPopupAnchor?: HTMLElement;
    selectedObject?: object;
    objectType?: string;
}

export const DataViewContainer = observer((props: Props) => {
    const { view: viewProp, updateAppTitle, criteria, overriddenTitle, breakpoint, onClick, style, children, args } = props;

    const [state, setState] = React.useState<State>({});
    const { error, objectPopupAnchor, selectedObject } = state;

    const [view, setView] = React.useState<DataView | undefined>();

    const getIdFromArgs = React.useCallback(
        (argsParam?: any[]) => {
            const argsList = argsParam || args;
            if (argsList && argsList.length >= 2 && "id" in argsList[1]) return argsList[1].id;
            return undefined;
        },
        [args]
    );

    const load = React.useCallback(
        async (viewName: string) => {
            let urlStr = `datagrid:/${viewName}`;

            if (urlStr.indexOf("{{id}}") > 0) {
                const id = getIdFromArgs();
                if (id) {
                    urlStr = urlStr.replace("{{id}}", id);
                } else {
                    console.error(`Can't resolve ${urlStr}`);
                    setState((prev) => ({ ...prev, error: "Missing id" }));
                    return;
                }
            }

            const bp = breakpoint ?? App().breakpoint;
            const newView = await DataView.loadAsync(urlStr, criteria, bp).catch((reason) => {
                console.error("error loading", reason);
                parseResponseError(reason).then((error) => setState((prev) => ({ ...prev, error })));
            });

            if (newView) {
                if (overriddenTitle) newView.data.title = overriddenTitle;
                if (updateAppTitle) App().title = newView.title;
                setView(newView);
            }
        },
        [breakpoint, criteria, overriddenTitle, updateAppTitle, getIdFromArgs]
    );

    // Effect to load initial view
    React.useEffect(() => {
        if (viewProp) {
            load(viewProp);
        }
    }, [viewProp, load]);

    // Effect to handle view prop changes (replaces UNSAFE_componentWillReceiveProps)
    React.useEffect(() => {
        if (viewProp && viewProp !== view?.baseUrl) {
            console.log(`view changed: ${viewProp} from ${view?.baseUrl}`);
            load(viewProp);
        }
    }, [viewProp, view?.baseUrl, load]);

    // Cleanup effect
    React.useEffect(() => {
        return () => {
            if (view) {
                view.unmount();
            }
        };
    }, [view]);

    const runEditActionAsync = React.useCallback(
        async (row: Object, field?: string | null): Promise<boolean> => {
            if (!view || !view.actions) return false;
            var editAction: Action | undefined = undefined;
            for (var action of view.actions) {
                if (action.name === "edit") {
                    editAction = action;
                    break;
                }
            }

            if (!editAction || !editAction.action) return false;

            if (await view.executeActionAsync(editAction, view.id(row))) {
                return true;
            }

            var evt: IEditEvent = {
                id: view.id(row),
                row,
                field,
                onClose: App().closeDataFormDialogAsync,
            };

            var result = Default.actions.execute(editAction.action, view.context, evt);
            if (!result) return false;

            if (result instanceof Form) {
                App().pushFormDialog({
                    form: result,
                    onActionAsync: view.onDialogActionAsync,
                });
            }

            return true;
        },
        [view]
    );

    const onAction = React.useCallback(
        async (action: IActionMenuItem, event: React.MouseEvent) => {
            if (!view) return;

            var id: string | undefined = undefined;
            switch (view.selectedCount) {
                case 0:
                    id = getIdFromArgs();
                    break;

                case 1:
                    id = view.selectedIds[0];
                    break;
            }

            if (typeof action.action === "string") {
                var actionPath = action.action;
                if (actionPath.indexOf("{{id}}") > 0 && id) {
                    action = {
                        ...action,
                        action: actionPath.replace("{{id}}", id),
                    };
                }
            }

            if (await view?.executeActionAsync(action, id)) {
                return true;
            }

            if (!action.action) {
                return false;
            }

            return Default.actions.execute(action.action, view.context, event);
        },
        [view, getIdFromArgs]
    );

    const handleClick = React.useCallback(
        async (e: React.MouseEvent, args: IOnClickArgs) => {
            const { row, field: fieldName, linkUrl } = args;
            e.stopPropagation();

            if (onClick) {
                const processed = onClick({
                    event: e,
                    row,
                    field: fieldName,
                });

                if (processed) return;
            }

            if (!view) return;

            const id = view.id(row);
            const field = view.fields.find((x) => x.name === fieldName);

            if (field && field.name && linkUrl) {
                const value = row[field.name];
                let url = linkUrl;

                const subst = linkUrl.match(/({{[^}]+}})/g) as string[];
                if (subst) {
                    for (const s of subst) {
                        if (s === "{{value}}") url = url.replace(s, value);
                        else if (s === "{{id}}") url = url.replace(s, id);
                        else {
                            const fieldName = s.substring(2, s.length - 2);
                            if (fieldName in row) {
                                url = url.replace(s, row[fieldName]);
                            }
                        }
                    }
                }

                await App().selectPageAsync(url, { id });
                return;
            }

            // hack to handle forms in the detail
            if (view.data.detail && view.data.detail.page) {
                let url = view.data.detail.page;
                const subst = url.match(/({{[^}]+}})/g) as string[];
                if (subst) {
                    for (const s of subst) {
                        if (s === "{{id}}") url = url.replace(s, id);
                        else {
                            const fieldName = s.substring(2, s.length - 2);
                            url = url.replace(s, row[fieldName]);
                        }
                    }
                }

                if (URI.isUri(url) && (await view?.handleUrlAsync(url, id, "detail"))) {
                    return;
                }
            }

            // handle master detail
            if (await view.showDetailAsync(row, fieldName, e.ctrlKey || e.metaKey)) return;

            // handle edit action if defined
            if (await runEditActionAsync(row, fieldName)) return;
        },
        [onClick, view, runEditActionAsync]
    );

    const loadFile = React.useCallback(
        (files: File[]) => {
            view?.uploadAsync(files);
        },
        [view]
    );

    const noopClick = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const onCloseFilterFormDialogAsync = React.useCallback(() => {
        if (!!view) {
            view.filterVisible = false;
        }
        return Promise.resolve();
    }, [view]);

    const renderFilterSidePanel = React.useCallback(() => {
        if (!view) {
            return null;
        }
        const { filterForm } = view;

        if (!!filterForm) {
            const body = filterForm.fields && filterForm.fields.length > 0 ? <FormBody key="filterForm" form={filterForm} autoFocus={false} /> : undefined;

            return (
                <div style={{ marginLeft: 12 }}>
                    <Paper key="leftPanel" style={{ padding: 12, marginBottom: 12 }}>
                        {body}
                        <DataViewFilterFormAutocomplete view={view} />
                        {view.isLoading && <Loading key="loading" />}
                    </Paper>
                </div>
            );
        }

        return (
            <Paper
                key="leftPanel"
                style={{
                    height: "100%",
                    overflow: "auto",
                    padding: "6px",
                    backgroundColor: "#e0e0e0",
                }}
            >
                <LocalFilterPanel key="filterPanel" dataView={view} fieldNames={view.filterableFields} />
            </Paper>
        );
    }, [view]);

    const renderFilterDialog = React.useCallback(() => {
        if (!view || !view.filterVisible) return null;

        const { filterForm } = view;

        if (!filterForm) {
            return <LocalFilterDialog key="filterDialog" dataView={view} />;
        }

        return (
            <Dialog
                className="FormDialog"
                title={filterForm.title}
                open={true}
                onClose={onCloseFilterFormDialogAsync}
                fullWidth={true}
                fullScreen={true}
                scroll="paper"
            >
                <DialogTitle>
                    <FormTitle form={filterForm} onClose={onCloseFilterFormDialogAsync} />
                </DialogTitle>

                <DialogContent>
                    <div>
                        <FormBody key="filterForm" form={filterForm} autoFocus={false} />
                        <DataViewFilterFormAutocomplete view={view} />
                        {view.isLoading && <Loading key="loading" />}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }, [view, onCloseFilterFormDialogAsync]);

    const onObjectMenu = React.useCallback((anchor: HTMLElement, row: object, objectType?: string) => {
        console.log("on object menu", toJS(row));
        setState((prev) => ({
            ...prev,
            objectPopupAnchor: anchor,
            selectedObject: row,
            objectType,
        }));
    }, []);

    const closeObjectPopup = React.useCallback(() => {
        setState((prev) => ({
            ...prev,
            objectPopupAnchor: undefined,
            selectedObject: undefined,
        }));
    }, []);

    const onSingleObjectAction = React.useCallback(
        (action: api.ActionMenuItem) => {
            console.log("action", action);

            closeObjectPopup();

            if (!selectedObject || !view) return;

            view.runSingleObjectActionAsync(action, selectedObject);
        },
        [selectedObject, view, closeObjectPopup]
    );

    if (error) return <Alert severity="error">{error}</Alert>;

    if (!view) {
        console.log("DataViewContainer::render, no view", props, view);

        return (
            <div>
                <LinearProgress style={{ width: "100%" }} />
            </div>
        );
    }

    const { isLoading, options } = view;
    const context: IDataViewContext = {
        dataView: view,
        onClick: handleClick,
        onObjectMenu: onObjectMenu,
    };

    const objectType = state.objectType ?? view.objectType;
    const objectId = !!objectType && selectedObject ? view.id(selectedObject) : undefined;

    const body = (
        <Box sx={{ height: options?.hideToolbar ? "100%" : "calc(100% - 64px)" }}>
            <DataViewContext.Provider value={context}>
                {options?.hideToolbar ? null : <DataViewToolbarComponent {...props} view={view} onAction={onAction} />}

                {children}

                {isLoading && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 100,
                            opacity: 0.2,
                            background: "white",
                        }}
                        onClick={noopClick}
                    />
                )}
                {isLoading && <Loading key="loadingAfter" />}
                <ObjectPopupMenu
                    key="objectPopupMenu"
                    anchor={objectPopupAnchor}
                    selectedObject={selectedObject}
                    onClose={closeObjectPopup}
                    actions={view.singleObjectActions}
                    onAction={onSingleObjectAction}
                    objectType={objectType}
                    objectId={objectId}
                />
            </DataViewContext.Provider>
        </Box>
    );

    const containerStyle = {
        display: "flex",
        height: "100%",
        maxWidth: "100%",
        ...style,
    };

    const bp = breakpoint ?? App().breakpoint;
    const showFilterSidePanel = bp !== api.ScreenBreakpoint.Xs;

    console.log("DataViewContainer::render", props);

    return (
        <>
            <Box key="body" style={containerStyle}>
                <FileDropTarget
                    key="fileDropTarget"
                    style={{ width: showFilterSidePanel && view.filterVisible ? "calc(100% - 300px)" : "100%" }}
                    onFilesDropped={view.canUpload ? loadFile : undefined}
                >
                    {body}
                </FileDropTarget>
                {showFilterSidePanel && view.filterVisible && <div style={{ width: 300 }}>{renderFilterSidePanel()}</div>}
            </Box>
            {!showFilterSidePanel && renderFilterDialog()}
        </>
    );
});
